import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MainLayout} from '../../layouts';
import {Table, Pagination, LoadingOverlay, Badge, Group, Text, Button, Modal, Alert, ActionIcon} from '@mantine/core';
import {Dropzone, MIME_TYPES} from '@mantine/dropzone';
import AdminApiClient from '../../../utils/http-clients/AdminApiClient';
import {ContentBox} from "../../partials";
import {
  Plus as PlusIcon,
  Upload as UploadIcon,
  AlertCircle as AlertCircleIcon,
  FileCheck as FileCheckIcon,
  FileExport as FileExportIcon,
  Refresh as RefreshIcon,
  PlayerPlay as StartIcon,
  Download as DownloadIcon,
  Trash as DeleteIcon,
  Eye as ViewIcon, AlertCircle
} from "tabler-icons-react";
import {useModals} from "@mantine/modals";

const BatchList = () => {
  const {t} = useTranslation();
  const modals = useModals()
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({current: 1, per: 10, last: 1});
  const [count, setCount] = useState(0);
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // View results modal state
  const [viewOpened, setViewOpened] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [viewData, setViewData] = useState<any>(null);

  const fetchBatchImports = async (page = 1) => {
    setIsLoading(true);
    try {
      const res = await AdminApiClient.getInstance().getBatchUserImports({page, per: pagination.per});
      const data = res.data.data;
      setItems(data.items || []);
      setPagination(data.pagination);
      setCount(data.count || 0);
    } catch (e) {
      // handle error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchImports(pagination.current);
    // eslint-disable-next-line
  }, [pagination.current]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({...prev, current: page}));
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const res = await AdminApiClient.getInstance().createBatchUserImport(formData);

      if (res.data.success) {
        setModalOpened(false);
        setSelectedFile(null);
        fetchBatchImports(pagination.current);
      }
    } catch (error: any) {
      setUploadError(error.response?.data?.message || t('Upload failed. Please try again.'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    setModalOpened(false);
    setSelectedFile(null);
    setUploadError(null);
  };

  const isValidFileType = (file: File) => {
    const validTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const validExtensions = ['.xls', '.xlsx'];
    return validTypes.includes(file.type) || validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
  };

  const handleFileChange = (files: File[]) => {
    const file = files[0];
    if (file && !isValidFileType(file)) {
      setUploadError(t('Please select a valid Excel file (.xls or .xlsx)'));
      return;
    }
    setSelectedFile(file);
    setUploadError(null);
  };

  const handleTemplateDownload = async () => {
    try {
      const res = await AdminApiClient.getInstance().downloadBatchUserTemplate();
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'batch-users-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download template:', error);
    }
  };

  const handleStartBatch = async (batchId: string) => {
    try {
      const res = await AdminApiClient.getInstance().startBatchUserImport(batchId);
      if (res.data.success) {
        fetchBatchImports(pagination.current);
      }
    } catch (error) {
      console.error('Failed to start batch:', error);
    }
  };

  const handleDownloadBatch = async (batchId: string) => {
    try {
      const res = await AdminApiClient.getInstance().downloadBatchUserImport(batchId);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `batch-users-results-${batchId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download batch results:', error);
    }
  };

  const handleDeleteBatch = async (batchId: string) => {
    modals.openConfirmModal({
      title: t('confirmation_required'),
      centered: true,
      children: <Text size="sm">{t('do_you_want_to_send')}</Text>,
      labels: {confirm: t('yes'), cancel: t('no')},
      confirmProps: {color: 'red'},
      onCancel: () => {
      },
      onConfirm: async () => {
        try {
          await AdminApiClient.getInstance().deleteBatchUserImport(batchId);
          fetchBatchImports(pagination.current);
        } catch (error) {
          console.error('Failed to delete batch:', error);
        }
      }
    })
  };

  const handleViewBatch = async (batchId: string) => {
    setViewOpened(true);
    setViewLoading(true);
    setViewError(null);
    setViewData(null);
    try {
      const res = await AdminApiClient.getInstance().getBatchUserImport(batchId);
      setViewData(res.data?.data);
    } catch (error: any) {
      setViewError(error?.response?.data?.message || 'Failed to load batch results');
    } finally {
      setViewLoading(false);
    }
  };

  const renderSection = (label: string, section: any, type: 'company' | 'location' | 'user') => {
    if (!section) return null;

    const items = section.items || [];
    const errors = section.errors || [];

    // Flatten errors to rows: [{ field, message }]
    const errorRows: Array<{ field: string; message: string }> = [];
    if (Array.isArray(errors)) {
      errors.forEach((er: any) => {
        if (typeof er === 'string') {
          errorRows.push({field: '-', message: er});
        } else if (er && typeof er === 'object') {
          Object.keys(er).forEach((k) => {
            const v = er[k];
            errorRows.push({field: k, message: typeof v === 'string' ? v : JSON.stringify(v)});
          });
        }
      });
    }

    return (
      <div style={{marginTop: 16}}>
        <Group spacing={8} align="center" mb={6}>
          <Text weight={600} size="sm">{label} ({t('total')}: {section.total ?? items.length})</Text>
          {errorRows.length > 0 && (
            <Badge color="red" size="sm" variant="filled">{errorRows.length}</Badge>
          )}
        </Group>
        <table style={{width: '100%', borderCollapse: 'collapse'}}>
          <thead>
          <tr>
            {type === 'company' && (<>
              <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '8px'}}>{t('id')}</th>
              <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '8px'}}>{t('name')}</th>
            </>)}
            {type === 'location' && (<>
              <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '8px'}}>{t('id')}</th>
              <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '8px'}}>{t('name')}</th>
              <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '8px'}}>{t('code')}</th>
            </>)}
            {type === 'user' && (<>
              <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '8px'}}>{t('id')}</th>
              <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '8px'}}>{t('name')}</th>
              <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '8px'}}>{t('lastname')}</th>
              <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '8px'}}>{t('email')}</th>
            </>)}
          </tr>
          </thead>
          <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={type === 'user' ? 4 : type === 'location' ? 3 : 2}>
                <Text align="center" size="sm">{t('errors.content_not_found')}</Text>
              </td>
            </tr>
          ) : items.map((it: any) => (
            <tr key={it.id}>
              {type === 'company' && (<>
                <td style={{borderBottom: '1px solid #f1f3f5', padding: '8px'}}>{it.id}</td>
                <td style={{borderBottom: '1px solid #f1f3f5', padding: '8px'}}>{it.name}</td>
              </>)}
              {type === 'location' && (<>
                <td style={{borderBottom: '1px solid #f1f3f5', padding: '8px'}}>{it.id}</td>
                <td style={{borderBottom: '1px solid #f1f3f5', padding: '8px'}}>{it.name}</td>
                <td style={{borderBottom: '1px solid #f1f3f5', padding: '8px'}}>{it.code}</td>
              </>)}
              {type === 'user' && (<>
                <td style={{borderBottom: '1px solid #f1f3f5', padding: '8px'}}>{it.id}</td>
                <td style={{borderBottom: '1px solid #f1f3f5', padding: '8px'}}>{it.name}</td>
                <td style={{borderBottom: '1px solid #f1f3f5', padding: '8px'}}>{it.last_name}</td>
                <td style={{borderBottom: '1px solid #f1f3f5', padding: '8px'}}>{it.email}</td>
              </>)}
            </tr>
          ))}
          </tbody>
        </table>
        {errorRows.length > 0 && (
          <Alert color="red" icon={<AlertCircleIcon size={16}/>} mt={10}>
            <Text weight={600} size="sm" mb={6}>Errors</Text>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
              <thead>
              <tr>
                <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '6px 8px'}}>Field</th>
                <th style={{textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '6px 8px'}}>Message</th>
              </tr>
              </thead>
              <tbody>
              {errorRows.map((row, idx) => (
                <tr key={`${row.field}-${idx}`}>
                  <td style={{borderBottom: '1px solid #f1f3f5', padding: '6px 8px'}}>{row.field}</td>
                  <td style={{borderBottom: '1px solid #f1f3f5', padding: '6px 8px'}}>{row.message}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </Alert>
        )}
      </div>
    );
  };

  return (
    <MainLayout>
      <Group position="apart" mb="md">
        <div>
          <ActionIcon variant="outline" color="orange" onClick={() => fetchBatchImports(pagination.current)}>
            <RefreshIcon size={18}/>
          </ActionIcon>
        </div>
        <div>
          <Button
            leftIcon={<FileExportIcon size={18}/>}
            variant="outline"
            color="blue"
            onClick={handleTemplateDownload}
          >
            {t('template')}
          </Button>
          <Button
            leftIcon={<PlusIcon size={18}/>}
            variant="outline"
            color="green"
            ml={5}
            onClick={() => setModalOpened(true)}
          >
            {t('import')}
          </Button>
        </div>
      </Group>

      <Alert icon={<AlertCircle size={16}/>} color="orange" withCloseButton>{t('import_users_notice')}</Alert>

      <Modal
        opened={modalOpened}
        onClose={handleModalClose}
        title={t('import')}
        size="md"
      >
        <div style={{padding: '20px 0'}}>
          {uploadError && (
            <Alert icon={<AlertCircleIcon size={16}/>} color="red" mb="md">
              {uploadError}
            </Alert>
          )}

          <Dropzone
            onDrop={handleFileChange}
            accept={[MIME_TYPES.xlsx, MIME_TYPES.xls]}
            multiple={false}
            loading={isUploading}
            style={{minHeight: 180, border: selectedFile ? '2px solid #51cf66' : undefined}}
          >
            {() => (
              <Group position="center" spacing="xl" style={{minHeight: 120}}>
                <div>
                  {selectedFile ? (
                    <FileCheckIcon size={50} color="#51cf66"/>
                  ) : (
                    <UploadIcon size={50} color="#868e96"/>
                  )}
                </div>
                <div>
                  <Text size="xl" inline>
                    {selectedFile ? selectedFile.name : t('drag_or_select_file')}
                  </Text>
                  <Text size="sm" color="dimmed" inline mt={7}>
                    {selectedFile ?
                      `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` :
                      t('accept_excel_files')
                    }
                  </Text>
                </div>
              </Group>
            )}
          </Dropzone>

          {selectedFile && (
            <Group mt="md" spacing="xs">
              <FileCheckIcon size={16} color="#51cf66"/>
              <Text size="sm" color="green">
                {t('File selected')}: {selectedFile.name}
              </Text>
            </Group>
          )}

          <Group position="right" mt="md">
            <Button variant="subtle" onClick={handleModalClose} disabled={isUploading}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleFileUpload}
              disabled={!selectedFile || isUploading}
              loading={isUploading}
              leftIcon={<UploadIcon size={16}/>}
            >
              {t('Upload')}
            </Button>
          </Group>
        </div>
      </Modal>

      {/* View results modal */}
      <Modal
        opened={viewOpened}
        onClose={() => setViewOpened(false)}
        title={t('results')}
        size="xl"
      >
        <div style={{paddingTop: 8, maxHeight: '70vh', overflowY: 'auto'}}>
          {viewLoading && (
            <Group position="center" my="md"><Text size="sm">{t('loading')}...</Text></Group>
          )}
          {viewError && (
            <Alert color="red" icon={<AlertCircleIcon size={16}/>}>{viewError}</Alert>
          )}
          {!viewLoading && !viewError && viewData && (
            <>
              <ContentBox>
                <Group position="apart" spacing="xs">
                  <div>
                    <Text size="sm"><strong>{t('id')}:</strong> {viewData.id}</Text>
                    <Text
                      size="sm"><strong>{t('user')}:</strong> {viewData.user ? `${viewData.user.name} ${viewData.user.last_name} (${viewData.user.email})` : '-'}
                    </Text>
                  </div>
                  <div>
                    <Group spacing={6}>
                      <Badge
                        color={
                          viewData.status === 'waiting' ? 'yellow' :
                            viewData.status === 'started' ? 'blue' :
                              viewData.status === 'progress' ? 'cyan' :
                                viewData.status === 'finished' ? 'green' :
                                  viewData.status === 'failed' ? 'red' : 'gray'
                        }
                      >
                        {viewData.status}
                      </Badge>
                    </Group>
                  </div>
                </Group>
                <Group spacing="md" mt={6}>
                  <Text size="sm"><strong>{t('created_at')}:</strong> {viewData.created_at}</Text>
                  <Text size="sm"><strong>{t('updated_at')}:</strong> {viewData.updated_at}</Text>
                </Group>
                {viewData.results?.message && (
                  <Alert color="green" icon={<FileCheckIcon size={16}/>} mt={10}>
                    <Text size="sm">{viewData.results.message}</Text>
                  </Alert>
                )}
              </ContentBox>

              <ContentBox withoutSpacing>
                <div style={{padding: 16}}>
                  {renderSection(t('companies'), viewData.results?.results?.company, 'company')}
                  {renderSection(t('locations'), viewData.results?.results?.location, 'location')}
                  {renderSection(t('users'), viewData.results?.results?.user, 'user')}
                </div>
              </ContentBox>
            </>
          )}
        </div>
      </Modal>

      <LoadingOverlay visible={isLoading}/>
      <>
        <ContentBox withoutSpacing>
          <Table highlightOnHover verticalSpacing="sm" mt="md">
            <thead>
            <tr>
              <th>{t('ID')}</th>
              <th>{t('user')}</th>
              <th className="text-center">{t('status')}</th>
              <th>{t('created_at')}</th>
              <th>{t('updated_at')}</th>
              <th className="text-center">{t('actions')}</th>
            </tr>
            </thead>
            <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <Text align="center">{t('errors.content_not_found')}</Text>
                </td>
              </tr>
            ) : (
              items.map((item: any) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.user ? `${item.user.name} ${item.user.last_name} (${item.user.email})` : '-'}</td>
                  <td className="text-center">
                    <Badge
                      color={
                        item.status === 'waiting' ? 'yellow' :
                          item.status === 'started' ? 'blue' :
                            item.status === 'progress' ? 'cyan' :
                              item.status === 'finished' ? 'green' :
                                item.status === 'failed' ? 'red' : 'gray'
                      }
                    >
                      {item.status}
                    </Badge>
                  </td>
                  <td>{item.created_at}</td>
                  <td>{item.updated_at}</td>
                  <td className="text-center">
                    <Group spacing="xs">
                      {item.status === 'waiting' && (
                        <ActionIcon color="green" onClick={() => handleStartBatch(item.id)}>
                          <StartIcon size={14}/>
                        </ActionIcon>
                      )}
                      {item.status === 'finished' && (
                        <ActionIcon color="cyan" onClick={() => handleDownloadBatch(item.id)}>
                          <DownloadIcon size={14}/>
                        </ActionIcon>
                      )}
                      <ActionIcon
                        color="blue"
                        onClick={() => handleViewBatch(item.id)}
                        disabled={item.status !== 'finished'}
                      >
                        <ViewIcon size={14}/>
                      </ActionIcon>
                      <ActionIcon color="red" onClick={() => handleDeleteBatch(item.id)}>
                        <DeleteIcon size={14}/>
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              ))
            )}
            </tbody>
          </Table>
        </ContentBox>
        <ContentBox>
          <Group position="apart" mt="md">
            <Text size="sm">{t('Total')}: {count}</Text>
            <Pagination page={pagination.current} onChange={handlePageChange} total={pagination.last}/>
          </Group>
        </ContentBox>
      </>
    </MainLayout>
  );
};

export default BatchList;
